import { useEffect, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { billingApi, companyApi, type CompanyUsage } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { Button, Card, ErrorNote, PageHeader } from '../ui';

type PaidPlan = 'plus' | 'professional' | 'business';

const PLANS: { key: PaidPlan; name: string; monthly: number; yearly: number; highlights: string[] }[] = [
  { key: 'plus', name: 'Plus', monthly: 12, yearly: 9, highlights: ['30 Rechnungen/Monat', '100 Belege/Monat', 'KI-Belegerkennung', 'Kassenbuch, EÜR', 'Mobile App'] },
  { key: 'professional', name: 'Professional', monthly: 29, yearly: 24, highlights: ['Unbegrenzte Rechnungen & Belege', 'Mahnwesen & Banking', 'Alle Steuermodule + ELSTER', 'BWA & Auswertungen'] },
  { key: 'business', name: 'Business', monthly: 59, yearly: 49, highlights: ['Alles aus Professional', 'Multi-Mandant & API', 'DATEV-Export', 'Dedizierter Support'] },
];

export default function Billing(): ReactElement {
  const { active, reload } = useCompany();
  const [params, setParams] = useSearchParams();
  const [usage, setUsage] = useState<CompanyUsage | null>(null);
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('yearly');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const status = params.get('status'); // success | cancel (von Stripe-Redirect)

  useEffect(() => {
    if (active) companyApi.usage(active.id).then(setUsage).catch(() => setUsage(null));
  }, [active]);

  // Nach erfolgreichem Checkout: Plan kann sich via Webhook geaendert haben -> neu laden.
  useEffect(() => {
    if (status === 'success') {
      reload();
      const t = setTimeout(() => {
        if (active) companyApi.usage(active.id).then(setUsage).catch(() => undefined);
      }, 1500);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function choose(plan: PaidPlan) {
    if (!active) return;
    setError(null);
    setBusy(plan);
    try {
      const url = await billingApi.checkout(active.id, plan, interval);
      window.location.href = url; // Weiterleitung zur Stripe-Bezahlseite
    } catch (e) {
      setError(e);
      setBusy(null);
    }
  }

  async function openPortal() {
    if (!active) return;
    setError(null);
    setBusy('portal');
    try {
      const url = await billingApi.portal(active.id);
      window.location.href = url;
    } catch (e) {
      setError(e);
      setBusy(null);
    }
  }

  const currentPlan = usage?.plan ?? active?.plan ?? 'starter';

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Tarif & Abrechnung"
        subtitle={`Dein aktueller Tarif: ${usage?.label ?? currentPlan}`}
        action={
          currentPlan !== 'starter' ? (
            <Button variant="secondary" onClick={openPortal} busy={busy === 'portal'}>
              Abo verwalten
            </Button>
          ) : undefined
        }
      />

      {usage?.subscription && usage.subscription.status !== 'none' && (
        <div className={`mb-4 rounded-xl px-4 py-3 text-sm ring-1 ${
          usage.subscription.status === 'past_due' ? 'bg-red-50 text-red-700 ring-red-100' : 'bg-gray-50 text-gray-700 ring-gray-100'
        }`}>
          {usage.subscription.status === 'active' && !usage.subscription.cancelAtPeriodEnd && (
            <>Abo aktiv{usage.subscription.currentPeriodEnd ? ` · verlängert sich am ${new Date(usage.subscription.currentPeriodEnd).toLocaleDateString('de-DE')}` : ''}.</>
          )}
          {usage.subscription.cancelAtPeriodEnd && usage.subscription.currentPeriodEnd && (
            <>Gekündigt – aktiv noch bis {new Date(usage.subscription.currentPeriodEnd).toLocaleDateString('de-DE')}.</>
          )}
          {usage.subscription.status === 'past_due' && <>⚠️ Zahlung fehlgeschlagen – bitte Zahlungsmethode aktualisieren (Abo verwalten).</>}
          {usage.subscription.status === 'canceled' && <>Abo beendet.</>}
        </div>
      )}

      {status === 'success' && (
        <div className="mb-4 rounded-xl bg-green-50 px-4 py-3 text-sm text-green-700 ring-1 ring-green-100">
          Vielen Dank! Dein Tarif wird aktiviert – das kann einen Moment dauern.
          <button onClick={() => setParams({})} className="ml-2 underline">ok</button>
        </div>
      )}
      {status === 'cancel' && (
        <div className="mb-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-amber-100">
          Vorgang abgebrochen – es wurde nichts berechnet.
          <button onClick={() => setParams({})} className="ml-2 underline">ok</button>
        </div>
      )}
      {error ? <div className="mb-4"><ErrorNote error={error} /></div> : null}

      {/* Monatlich / Jaehrlich */}
      <div className="mb-6 flex items-center justify-center gap-3">
        <span className={interval === 'monthly' ? 'font-semibold' : 'text-gray-500'}>Monatlich</span>
        <button
          onClick={() => setInterval((i) => (i === 'monthly' ? 'yearly' : 'monthly'))}
          className={`relative h-7 w-14 rounded-full transition ${interval === 'yearly' ? 'bg-blue-600' : 'bg-gray-300'}`}
        >
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition ${interval === 'yearly' ? 'left-8' : 'left-1'}`} />
        </button>
        <span className={interval === 'yearly' ? 'font-semibold' : 'text-gray-500'}>
          Jährlich <span className="text-green-600">(2 Monate gratis)</span>
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {PLANS.map((p) => {
          const isCurrent = currentPlan === p.key;
          const price = interval === 'yearly' ? p.yearly : p.monthly;
          return (
            <Card key={p.key} className={isCurrent ? 'ring-2 ring-blue-500' : ''}>
              <div className="text-lg font-bold text-gray-900">{p.name}</div>
              <div className="mt-2">
                <span className="text-3xl font-bold text-gray-900">{price} €</span>
                <span className="text-sm text-gray-500"> / Monat{interval === 'yearly' ? ' (jährl.)' : ''}</span>
              </div>
              <ul className="mt-4 space-y-1.5 text-sm text-gray-600">
                {p.highlights.map((h) => (
                  <li key={h} className="flex gap-2"><span className="text-green-500">✓</span>{h}</li>
                ))}
              </ul>
              <div className="mt-6">
                {isCurrent ? (
                  <Button variant="secondary" disabled>Aktueller Tarif</Button>
                ) : (
                  <Button onClick={() => choose(p.key)} busy={busy === p.key}>
                    {p.name} wählen
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
